import {
  doc,
  setDoc,
  getDoc,
  collection,
  serverTimestamp,
  runTransaction,
  updateDoc,
} from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { Organization, OrganizationMember, COLLECTIONS, ROLE_PERMISSIONS } from '@prepro/shared'
import { useAuthStore } from '@/store/auth'

export const organizationService = {
  // Create a new organization
  async createOrganization(data: {
    name: string
    slug: string
  }) {
    const { firebaseUser } = useAuthStore.getState()
    if (!firebaseUser) throw new Error('User not authenticated')

    const orgId = doc(collection(db, COLLECTIONS.ORGANIZATIONS)).id
    
    return runTransaction(db, async (transaction) => {
      // Check if slug is already taken
      const slugQuery = doc(db, 'slugs', data.slug)
      const slugDoc = await transaction.get(slugQuery)
      
      if (slugDoc.exists()) {
        throw new Error('Organization URL is already taken')
      }
      
      // Create organization
      const orgRef = doc(db, COLLECTIONS.ORGANIZATIONS, orgId)
      const organization: Omit<Organization, 'id'> = {
        name: data.name,
        slug: data.slug,
        plan: 'producer',
        createdAt: serverTimestamp() as any,
        ownerId: firebaseUser.uid,
        colors: {
          primary: '#1976d2',
          secondary: '#dc004e',
        },
        settings: {
          timezone: 'America/New_York',
          dateFormat: 'MM/DD/YYYY',
          currency: 'USD',
          allowGuestAccess: false,
        },
        memberCount: 1,
        eventCount: 0,
      }
      
      transaction.set(orgRef, organization)
      
      // Reserve slug
      transaction.set(slugQuery, {
        organizationId: orgId,
        createdAt: serverTimestamp(),
      })
      
      // The Cloud Function will automatically create the member document
      // when the organization is created
      
      return orgId
    })
  },

  // Get organization by ID
  async getOrganization(orgId: string): Promise<Organization | null> {
    const orgDoc = await getDoc(doc(db, COLLECTIONS.ORGANIZATIONS, orgId))
    
    if (!orgDoc.exists()) {
      return null
    }
    
    return {
      id: orgDoc.id,
      ...orgDoc.data(),
    } as Organization
  },

  // Check if user has permission in organization
  hasPermission(orgId: string, permission: string): boolean {
    const { organizationMemberships } = useAuthStore.getState()
    const membership = organizationMemberships.find((m) => m.organizationId === orgId)
    
    if (!membership) return false
    
    return (
      membership.permissions.includes('*') ||
      membership.permissions.includes(permission)
    )
  },

  // Check if user can update organization (role-based check)
  canUpdateOrganization(orgId: string): boolean {
    const { organizationMemberships } = useAuthStore.getState()
    const membership = organizationMemberships.find((m) => m.organizationId === orgId)

    if (!membership) return false

    // Allow owners and administrators to update organization settings
    return membership.role === 'owner' || membership.role === 'administrator'
  },

  // Get current organization
  getCurrentOrganization(): string | null {
    return useAuthStore.getState().currentOrganizationId
  },

  // Switch organization
  switchOrganization(orgId: string) {
    const { organizationMemberships } = useAuthStore.getState()
    const membership = organizationMemberships.find((m) => m.organizationId === orgId)
    
    if (!membership) {
      throw new Error('You are not a member of this organization')
    }
    
    useAuthStore.getState().setCurrentOrganization(orgId)
    
    // Persist to localStorage
    localStorage.setItem('currentOrganizationId', orgId)
  },
  
  // Check if user is member of organization (direct query)
  async checkMembership(orgId: string, userId: string): Promise<OrganizationMember | null> {
    try {
      const memberDoc = await getDoc(
        doc(db, COLLECTIONS.ORGANIZATIONS, orgId, COLLECTIONS.MEMBERS, userId)
      )
      
      if (!memberDoc.exists()) {
        return null
      }
      
      return {
        ...memberDoc.data(),
        organizationId: orgId,
      } as OrganizationMember
    } catch (error) {
      console.error('Error checking membership:', error)
      return null
    }
  },

  // Update organization
  async updateOrganization(orgId: string, data: Partial<Organization>): Promise<void> {
    const { user } = useAuthStore.getState()
    if (!user) throw new Error('User not authenticated')

    // Check permissions - allow owners and administrators to update organization settings
    if (!this.canUpdateOrganization(orgId)) {
      throw new Error('You do not have permission to update this organization')
    }

    const updateData: any = {
      updatedAt: serverTimestamp(),
    }

    // Only include defined fields
    if (data.name !== undefined) updateData.name = data.name
    if ('phone' in data) updateData.phone = data.phone || null
    if ('email' in data) updateData.email = data.email || null
    if ('website' in data) updateData.website = data.website || null
    if ('address' in data) updateData.address = data.address || null
    if (data.colors !== undefined) updateData.colors = data.colors
    
    // ALWAYS include logo fields if they are provided
    if ('logoUrl' in data) {
      updateData.logoUrl = data.logoUrl === null ? null : data.logoUrl
    }
    if ('darkModeLogoUrl' in data) {
      updateData.darkModeLogoUrl = data.darkModeLogoUrl === null ? null : data.darkModeLogoUrl
    }
    if ('collapsedLogoUrl' in data) {
      updateData.collapsedLogoUrl = data.collapsedLogoUrl === null ? null : data.collapsedLogoUrl
    }
    if ('collapsedDarkModeLogoUrl' in data) {
      updateData.collapsedDarkModeLogoUrl = data.collapsedDarkModeLogoUrl === null ? null : data.collapsedDarkModeLogoUrl
    }
    if ('faviconUrl' in data) {
      updateData.faviconUrl = data.faviconUrl === null ? null : data.faviconUrl
    }
    
    // Brand assets
    if ('brandAssets' in data) updateData.brandAssets = (data as any).brandAssets

    // CRITICAL: Handle settings with dot notation to preserve existing nested data
    if (data.settings !== undefined) {
      // First, get the current settings to merge with
      const currentOrg = await this.getOrganization(orgId)
      if (currentOrg) {
        updateData.settings = {
          ...currentOrg.settings,  // Preserve existing settings
          ...data.settings,        // Apply new settings
          updatedAt: serverTimestamp(),
        }
      } else {
        updateData.settings = {
          ...data.settings,
          updatedAt: serverTimestamp(),
        }
      }
    }
    
    console.log('Final updateData being sent to Firestore:', updateData)
    
    await updateDoc(doc(db, COLLECTIONS.ORGANIZATIONS, orgId), updateData)
  },
}